import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Building2, MessageCircle } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Seo from "@/components/seo/Seo";
import { COMMUNITY_LIST, COMMUNITY_HUB } from "@/data/communityPages";
import { breadcrumbSchema, faqSchema, SITE_URL } from "@/lib/seo";
import { REPORT_PROMISE, waInspectorHref } from "@/lib/business";

const HUB_FAQS = [
  { q: "Which Dubai communities does Fedari inspect?", a: "We carry out independent snagging, handover and DLP inspections across Dubai's active handover communities — including Al Furjan, Sobha Hartland (and Hartland II), Sobha One, Sobha Reserve, Central Park at City Walk, Madinat Jumeirah Living, JLT, DAMAC Lagoons, Azizi Riviera, Dubai Hills Estate, Dubai Creek Harbour, Town Square, Arabian Ranches 3, Nad Al Sheba Gardens and Mudon Al Ranim. If your community is not listed, contact us and we will confirm availability." },
  { q: "Do you inspect apartments, townhouses and villas?", a: "Yes. Our scope adapts to the property — apartments, townhouses and villas — including safe, accessible external areas and roofing for villas." },
  { q: "How soon will I get my report?", a: `Your photo-led report is ${REPORT_PROMISE}.` },
  { q: "Can you inspect if I am an overseas owner?", a: "Yes. Once lawful and safe access is arranged we can inspect on your behalf and deliver the report digitally." },
];

export default function CommunitiesHub() {
  const path = COMMUNITY_HUB;
  const title = "Dubai Community Property Snagging | Fedari Inspection";
  const description =
    "Independent property snagging and handover inspections across Dubai communities — Al Furjan, Sobha Hartland, City Walk, JLT, DAMAC Lagoons and more. Report delivery within the confirmed timeframe.";

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Dubai communities served by Fedari Inspection",
    itemListElement: COMMUNITY_LIST.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      url: `${SITE_URL}${c.href}`,
    })),
  };
  const jsonLd = [
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Dubai Communities", path }]),
    itemList,
    faqSchema(HUB_FAQS),
  ];

  return (
    <PageShell breadcrumbs={[{ name: "Dubai Communities", path }]}>
      {({ onQuote }) => (
        <>
          <Seo title={title} description={description} path={path} jsonLd={jsonLd} />

          {/* Hero */}
          <section className="wrap pt-6 pb-10 lg:pt-10 lg:pb-12">
            <span className="eyebrow">Property inspection by community · Dubai</span>
            <h1 className="text-[clamp(1.9rem,4vw,3rem)] leading-[1.07] tracking-[-0.03em] mt-3 mb-5 max-w-[22ch]">Property snagging across Dubai communities</h1>
            <p className="text-[1.1rem] text-bodytext max-w-[64ch] mb-4">
              Choose your community for handover-ready guidance, an inspection scope tailored to the property types built there, and a photo-led report {REPORT_PROMISE}.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-6">
              <button onClick={onQuote} data-testid="hub-quote-btn"
                className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                Get quote &amp; availability <ArrowRight size={17} />
              </button>
              <a href={waInspectorHref} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold bg-white hover:border-wa hover:text-wa transition-colors">
                <MessageCircle size={17} /> WhatsApp Fedari
              </a>
            </div>
          </section>

          {/* Community grid */}
          <section className="wrap pb-14">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-grid">
              {COMMUNITY_LIST.map((c) => (
                <Link key={c.slug} to={c.href} data-testid={`community-card-${c.slug}`}
                  className="group border border-line rounded-2xl p-5 bg-white hover:border-blue hover:shadow-sm transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-blue font-mono-jb uppercase tracking-wide">{c.intent}</span>
                    <ArrowRight size={16} className="text-line group-hover:text-blue transition-colors" />
                  </div>
                  <h2 className="text-lg font-display mb-2 group-hover:text-blue transition-colors">{c.name}</h2>
                  <div className="flex items-center gap-1.5 text-sm text-muted mb-1"><Building2 size={13} className="text-blue" /> {c.developer}</div>
                  <div className="flex items-center gap-1.5 text-sm text-muted mb-3"><MapPin size={13} className="text-blue" /> {c.emirate}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {c.propertyTypes.map((p) => (
                      <span key={p} className="px-2.5 py-1 rounded-full border border-line text-xs text-bodytext bg-soft">{p}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="wrap pb-16">
            <h2 className="text-2xl mb-5">Community inspections — FAQs</h2>
            <div className="space-y-4 max-w-[80ch]">
              {HUB_FAQS.map((f, i) => (
                <div key={i} className="border border-line rounded-xl p-5">
                  <h3 className="font-semibold text-ink mb-2">{f.q}</h3>
                  <p className="text-bodytext text-[0.95rem]">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
